import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHotMovies, getGenreLabel } from "@/lib/movie";

export default function HomeHero() {
  const slides = getHotMovies(5);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden bg-primary-950">
      {slides.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 电影剧照背景`}
            title={`${movie.title} - 最新电影推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/30 to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-end">
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 pb-16 md:pb-24">
          <div className="max-w-2xl">
            <h1 className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-4">
              <span className="font-heading font-black text-lg md:text-xl text-background-50">
                最新电影
              </span>
              <span className="w-px h-4 bg-background-100/30"></span>
              <span className="text-[13px] md:text-sm text-background-200">
                高清电影资讯与上映动态平台
              </span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-background-50 bg-accent-500 rounded-full px-3 py-1">
                <i className="ri-fire-fill"></i>
                本周热映推荐
              </span>
              {slides[index].genres.map((g) => (
                <span
                  key={g}
                  className="text-xs text-background-100 border border-background-100/40 rounded-full px-3 py-1"
                >
                  {getGenreLabel(g)}
                </span>
              ))}
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-black text-background-50 leading-tight">
              {slides[index].title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-secondary-300 italic">
              “{slides[index].tagline}”
            </p>
            <p className="mt-4 text-sm md:text-[15px] text-background-200 leading-relaxed line-clamp-2 md:line-clamp-3">
              {slides[index].synopsis}
            </p>
            <div className="flex flex-wrap items-center gap-3 md:gap-5 mt-6 text-sm text-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-secondary-400"></i>
                <strong className="text-background-50 text-base">{slides[index].rating.toFixed(1)}</strong>
                <span className="text-background-400">/ 10</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {slides[index].releaseDate} 上映
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {slides[index].duration} 分钟
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link
                to={`/detail/${slides[index].id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${slides[index].category}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/10 border border-background-100/40 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                同类影片
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        aria-label="上一部"
        onClick={() => go(-1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一部"
        onClick={() => go(1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* indicators */}
      <div className="absolute top-20 md:top-24 right-4 md:right-6 z-20 flex items-center gap-2">
        {slides.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}